import { useMemo } from 'react';
import { BookOpen, GitBranch, Layers } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  missingSuggestedArtifacts,
  programmingContextFromIntake,
} from '@/lib/learningTutorial/programmingContext';

import { TwinneeLogo } from '@/components/TwinneeLogo';
const ARTIFACT_COLORS = {
  explanation: 'text-sky-300 border-sky-500/40 bg-sky-950/40',
  diagram: 'text-emerald-300 border-emerald-500/40 bg-emerald-950/40',
  flowchart: 'text-emerald-300 border-emerald-500/40 bg-emerald-950/40',
  example: 'text-amber-300 border-amber-500/40 bg-amber-950/40',
  analogy: 'text-amber-300 border-amber-500/40 bg-amber-950/40',
  code: 'text-violet-300 border-violet-500/40 bg-violet-950/40',
  dryrun: 'text-cyan-300 border-cyan-500/40 bg-cyan-950/40',
  memory: 'text-indigo-300 border-indigo-500/40 bg-indigo-950/40',
  default: 'text-slate-300 border-slate-600/50 bg-slate-800/40',
};

function artifactStyle(type = '') {
  const t = String(type).toLowerCase();
  if (t.includes('code')) return ARTIFACT_COLORS.code;
  if (t.includes('flowchart') || t.includes('diagram') || t.includes('graph') || t.includes('map')) {
    return ARTIFACT_COLORS.diagram;
  }
  if (t.includes('analogy') || t.includes('example') || t.includes('case study') || t.includes('use case')) {
    return ARTIFACT_COLORS.example;
  }
  if (t.includes('dry run') || t.includes('dry_run')) return ARTIFACT_COLORS.dryrun;
  if (t.includes('memory')) return ARTIFACT_COLORS.memory;
  if (t.includes('explanation') || t.includes('walkthrough') || t.includes('step')) {
    return ARTIFACT_COLORS.explanation;
  }
  return ARTIFACT_COLORS.default;
}

function TreeLines({ depth, isLast, ancestorLast }) {
  if (depth === 0) return null;
  return (
    <span className="inline-flex shrink-0 select-none" aria-hidden>
      {ancestorLast.slice(1).map((last, i) => (
        <span key={i} className="w-5 inline-block text-slate-600">
          {last ? ' ' : '│'}
        </span>
      ))}
      <span className="w-5 inline-block text-slate-500">{isLast ? '└── ' : '├── '}</span>
    </span>
  );
}

function ConceptNode({ title, summary, depth, isLast, ancestorLast }) {
  return (
    <div className="flex items-start gap-1 min-w-0">
      <TreeLines depth={depth} isLast={isLast} ancestorLast={ancestorLast} />
      <div className="flex items-start gap-2 min-w-0 py-0.5">
        <BookOpen className="w-3.5 h-3.5 text-violet-400 shrink-0 mt-0.5" />
        <div className="min-w-0">
          <span className="text-sm font-medium text-violet-100">{title}</span>
          {summary ? (
            <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{summary}</p>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function TrainerNarrationExcerpt({ text, label = 'Trainer script' }) {
  const narration = String(text || '').trim();
  if (!narration) return null;
  return (
    <details className="mt-1 ml-0 group">
      <summary className="text-[10px] uppercase tracking-wide text-amber-500/80 cursor-pointer list-none flex items-center gap-1">
        <span className="group-open:rotate-90 transition-transform inline-block">▸</span>
        {label}
        <span className="text-slate-600 normal-case">({narration.length} chars)</span>
      </summary>
      <p className="text-xs text-slate-400 mt-1 pl-3 border-l border-amber-500/20 whitespace-pre-wrap leading-relaxed">
        {narration}
      </p>
    </details>
  );
}

function ArtifactNode({ artifact, depth, isLast, ancestorLast }) {
  const type = artifact.artifact_type || artifact.type || 'Artifact';
  const heading = artifact.heading || type;
  const layer = artifact.layer ? String(artifact.layer).replace(/_/g, ' ') : '';
  const trainerNarration = artifact.trainer_narration;
  const subtitle = trainerNarration
    ? artifact.on_screen_text || artifact.purpose
    : artifact.purpose;
  return (
    <div className="flex items-start gap-1 min-w-0">
      <TreeLines depth={depth} isLast={isLast} ancestorLast={ancestorLast} />
      <div className="flex items-start gap-2 min-w-0 py-0.5">
        <TwinneeLogo className="w-3.5 h-3.5 text-amber-400 shrink-0 mt-0.5" />
        <div className="min-w-0">
          <span
            className={cn(
              'inline-flex items-center gap-1.5 text-xs px-1.5 py-0.5 rounded border',
              artifactStyle(type),
            )}
          >
            {type}
          </span>
          <span className="text-sm text-slate-200 ml-2">{heading}</span>
          {layer ? <span className="text-xs text-slate-500 ml-2">({layer})</span> : null}
          {subtitle ? (
            <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{subtitle}</p>
          ) : null}
          <TrainerNarrationExcerpt text={trainerNarration} />
        </div>
      </div>
    </div>
  );
}

function TopicTreeNode({ node, depth, isLast, ancestorLast }) {
  const kind = node.node_kind || 'concept';
  const isArtifact = kind === 'artifact';
  const title = node.title || 'Untitled';
  const type = node.artifact_type || '';

  if (isArtifact) {
    return (
      <ArtifactNode
        artifact={{
          artifact_type: type,
          heading: title,
          layer: node.layer,
          purpose: node.explain,
          trainer_narration: node.trainer_narration,
          on_screen_text: node.on_screen_text,
        }}
        depth={depth}
        isLast={isLast}
        ancestorLast={ancestorLast}
      />
    );
  }

  return (
    <div>
      <ConceptNode
        title={title}
        summary={node.on_screen_text || node.explain || node.summary}
        depth={depth}
        isLast={isLast && !(node.children?.length)}
        ancestorLast={ancestorLast}
      />
      <TrainerNarrationExcerpt text={node.trainer_narration} />
      {(node.children || []).map((child, idx, arr) => (
        <TopicTreeBranch
          key={`${depth}-${title}-${child.title || idx}`}
          node={child}
          depth={depth + 1}
          isLast={idx === arr.length - 1}
          ancestorLast={[...ancestorLast, isLast]}
        />
      ))}
    </div>
  );
}

function TopicTreeBranch({ node, depth, isLast, ancestorLast }) {
  return <TopicTreeNode node={node} depth={depth} isLast={isLast} ancestorLast={ancestorLast} />;
}

function ArtifactBranch({ artifact, depth, isLast, ancestorLast }) {
  const steps = artifact?.dry_run?.steps || [];
  const hasSubtree = steps.length > 0
    || String(artifact?.artifact_type || '').toLowerCase().includes('dry run');
  return (
    <div>
      <ArtifactNode
        artifact={artifact}
        depth={depth}
        isLast={isLast && !steps.length}
        ancestorLast={ancestorLast}
      />
      {hasSubtree && steps.map((step, idx) => (
        <ArtifactNode
          key={`dr-${artifact.heading || 'step'}-${step.heading || idx}`}
          artifact={{
            artifact_type: step.step_type || 'DRY_RUN_STEP',
            heading: step.heading || step.step_type,
            layer: 'practical_understanding',
            purpose: step.narration_hint,
          }}
          depth={depth + 1}
          isLast={idx === steps.length - 1}
          ancestorLast={[...ancestorLast, isLast]}
        />
      ))}
    </div>
  );
}

function SuggestedArtifactHint({ gaps }) {
  if (!gaps?.length) return null;
  return (
    <p className="text-[10px] text-amber-400/90 mt-1 ml-8 pl-2 border-l border-amber-500/30">
      Suggested (optional): {gaps.join(' · ')}
    </p>
  );
}

function ExpandedConceptBranch({ node, depth, isLast, ancestorLast, intake }) {
  const title = node.title || 'Untitled';
  const artifacts = node.instructional_artifacts || [];
  const children = node.children || [];
  const childRows = [
    ...artifacts.map((a) => ({ kind: 'artifact', data: a })),
    ...children.map((c) => ({ kind: 'concept', data: c })),
  ];
  const progCtx = programmingContextFromIntake(intake || {}, title);
  const suggestedGaps = missingSuggestedArtifacts(artifacts, progCtx);

  return (
    <div>
      <ConceptNode
        title={title}
        summary={node.summary}
        depth={depth}
        isLast={isLast && childRows.length === 0}
        ancestorLast={ancestorLast}
      />
      {suggestedGaps.length > 0 ? (
        <SuggestedArtifactHint gaps={suggestedGaps} />
      ) : null}
      {childRows.map((row, idx) => {
        const last = idx === childRows.length - 1;
        const branchAncestor = [...ancestorLast, isLast];
        if (row.kind === 'artifact') {
          return (
            <ArtifactBranch
              key={`art-${title}-${row.data.heading || idx}`}
              artifact={row.data}
              depth={depth + 1}
              isLast={last}
              ancestorLast={branchAncestor}
            />
          );
        }
        return (
          <ExpandedConceptBranch
            key={`concept-${title}-${row.data.title || idx}`}
            node={row.data}
            depth={depth + 1}
            isLast={last}
            ancestorLast={branchAncestor}
            intake={intake}
          />
        );
      })}
    </div>
  );
}

function ConceptTreeBranch({ node, depth, isLast, ancestorLast }) {
  const title = node.title || 'Untitled';
  const children = node.children || [];
  return (
    <div>
      <ConceptNode
        title={title}
        summary={node.summary}
        depth={depth}
        isLast={isLast && children.length === 0}
        ancestorLast={ancestorLast}
      />
      {children.map((child, idx) => (
        <ConceptTreeBranch
          key={`${depth}-${title}-${child.title || idx}`}
          node={child}
          depth={depth + 1}
          isLast={idx === children.length - 1}
          ancestorLast={[...ancestorLast, isLast]}
        />
      ))}
    </div>
  );
}

function DfsPreviewList({ items }) {
  if (!items?.length) {
    return <p className="text-sm text-slate-500">No DFS preview available.</p>;
  }
  return (
    <ol className="space-y-1 font-mono text-xs">
      {items.map((row, idx) => (
        <li key={idx} className="flex items-center gap-2 text-slate-300">
          <span className="text-slate-600 w-6 text-right shrink-0">{idx + 1}.</span>
          <span className="text-violet-400 shrink-0">POP</span>
          <span className="text-slate-200">{row.node}</span>
          {row.node_kind ? (
            <span className={cn(
              'text-[10px] px-1.5 py-0.5 rounded border',
              row.node_kind === 'artifact' ? ARTIFACT_COLORS.example : 'text-violet-300 border-violet-500/30',
            )}
            >
              {row.node_kind}
            </span>
          ) : null}
        </li>
      ))}
    </ol>
  );
}

function passLabel(pipelinePass) {
  if (pipelinePass >= 3) return 'Pass 3 — script embedded';
  if (pipelinePass >= 2) return 'Pass 2+ — concepts with instructional_artifacts';
  return 'Pass 1 — concept hierarchy (artifacts pending)';
}

export function extractTreePayload(tabId, data) {
  if (!data) return { kind: 'empty' };
  switch (tabId) {
    case 'pass1':
      return {
        kind: 'expanded',
        roots: data.concept_tree || [],
        label: data.topic,
        subtitle: 'Pass 1 — knowledge decomposition (concept hierarchy only)',
      };
    case 'pass2':
      return {
        kind: 'expanded',
        roots: data.concept_tree || [],
        label: data.topic,
        subtitle: 'Pass 2 — instructional_artifacts added to each concept',
      };
    case 'pass3':
      return {
        kind: 'topic',
        roots: data.script?.scenes?.[0]?.topic_tree
          || data.derived?.topic_tree
          || [],
        label: data.topic,
        subtitle: 'Pass 3 — trainer narration on DFS teach tree',
      };
    case 'orchestration':
      return {
        kind: 'expanded',
        roots: data.concept_tree || [],
        label: data.topic,
        subtitle: passLabel(data.pipeline_pass || 1),
      };
    case 'derived_tree':
      return {
        kind: 'topic',
        roots: data.derived?.topic_tree || [],
        label: data.topic,
        subtitle: 'Derived DFS topic tree (artifacts as child nodes)',
      };
    case 'dfs':
      return { kind: 'dfs', items: data.derived?.dfs_stack_preview || data };
    case 'script':
      return {
        kind: 'topic',
        roots: data.script?.scenes?.[0]?.topic_tree || data.scenes?.[0]?.topic_tree || [],
        label: data.script?.title || data.title || data.topic,
        subtitle: 'Pass 3 trainer script — full narration per DFS node',
      };
    default:
      return { kind: 'empty' };
  }
}

export function OrchestrationHierarchyTree({ tabId, data, intake: intakeProp }) {
  const resolvedIntake = intakeProp || data?.intake || {};
  const payload = useMemo(() => extractTreePayload(tabId, data), [tabId, data]);

  if (payload.kind === 'empty') {
    return <p className="text-sm text-slate-500">No hierarchy data for this tab.</p>;
  }

  if (payload.kind === 'dfs') {
    return (
      <div>
        <p className="text-xs text-slate-500 mb-3 flex items-center gap-2">
          <GitBranch className="w-3.5 h-3.5" />
          Stack POP order — teaching sequence
        </p>
        <DfsPreviewList items={payload.items} />
      </div>
    );
  }

  const roots = payload.roots || [];
  if (!roots.length) {
    return <p className="text-sm text-slate-500">Tree is empty.</p>;
  }

  return (
    <div>
      {payload.label ? (
        <p className="text-xs text-slate-500 mb-3 flex items-center gap-2">
          <Layers className="w-3.5 h-3.5 text-violet-400" />
          <span className="text-violet-200 font-medium">{payload.label}</span>
          <span className="text-slate-600">·</span>
          <span>{payload.subtitle || (
            payload.kind === 'expanded' ? 'Unified concept_tree' : 'DFS topic tree'
          )}</span>
        </p>
      ) : null}
      <div className="font-mono text-[13px] leading-relaxed">
        {roots.map((root, idx) => {
          const key = root.title || idx;
          if (payload.kind === 'expanded') {
            return (
              <ExpandedConceptBranch
                key={key}
                node={root}
                depth={0}
                isLast={idx === roots.length - 1}
                ancestorLast={[true]}
                intake={resolvedIntake}
              />
            );
          }
          if (payload.kind === 'topic') {
            return (
              <TopicTreeBranch
                key={key}
                node={root}
                depth={0}
                isLast={idx === roots.length - 1}
                ancestorLast={[true]}
              />
            );
          }
          return (
            <ConceptTreeBranch
              key={key}
              node={root}
              depth={0}
              isLast={idx === roots.length - 1}
              ancestorLast={[true]}
            />
          );
        })}
      </div>
    </div>
  );
}
